import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import { NoteState } from "../../context/notes/NoteState";
import "./announcements.css";

const EMPTY_FORM = {
  title: "",
  description: "",
  priority: "Medium",
  announced_by: "",
  visible_from: "",
  visible_until: "",
};

const extractRows = (response) => {
  const payload = response?.data?.data;

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;

  return [];
};

const formatDateTime = (value) => {
  if (!value) return "N/A";

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const priorityClass = (value) => `announcement-priority ${String(value || "").toLowerCase()}`;

const statusClass = (value) => `announcement-status ${String(value || "active").toLowerCase()}`;

const isLive = (row) => {
  const now = new Date();
  const from = row?.visible_from ? new Date(row.visible_from) : null;
  const until = row?.visible_until ? new Date(row.visible_until) : null;

  if (row?.status !== "1") return false;
  if (from && from > now) return false;
  if (until && until < now) return false;
  return true;
};

export default function AnnouncementManager() {
  const { state } = useContext(NoteState);
  const { branch_id } = state;
  const normalizedBranchId =
    branch_id !== null && branch_id !== undefined && String(branch_id) !== "0" && String(branch_id) !== ""
      ? branch_id
      : null;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("2");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [announcements, setAnnouncements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const selectedAnnouncement = useMemo(
    () => announcements.find((row) => String(row.id) === String(selectedId)) || null,
    [announcements, selectedId]
  );

  const loadAnnouncements = useCallback(async () => {
    setLoading(true);

    try {
      const response = await UserServices.getAnnouncements({
        branch_id: normalizedBranchId,
        search,
        priority: priorityFilter,
        status: statusFilter,
        limit: 100,
        page: 1,
      });
      const rows = extractRows(response);
      setAnnouncements(rows);
      setSelectedId((current) =>
        rows.find((row) => String(row.id) === String(current)) ? current : rows[0]?.id || null
      );
    } catch (error) {
      if (error?.response?.status === 404) {
        setAnnouncements([]);
        setSelectedId(null);
      } else {
        toast.error(error?.response?.data?.message || "Unable to load announcements.");
      }
    } finally {
      setLoading(false);
    }
  }, [normalizedBranchId, priorityFilter, search, statusFilter]);

  useEffect(() => {
    loadAnnouncements();
  }, [loadAnnouncements]);

  const resetForm = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
  };

  const beginEdit = (row) => {
    setEditId(row.id);
    setSelectedId(row.id);
    setForm({
      title: row.title || "",
      description: row.description || "",
      priority: row.priority || "Medium",
      announced_by: row.announced_by || "",
      visible_from: row.visible_from ? String(row.visible_from).slice(0, 16) : "",
      visible_until: row.visible_until ? String(row.visible_until).slice(0, 16) : "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSave = async (event) => {
    event.preventDefault();

    if (!form.title.trim() || !form.description.trim() || !form.announced_by.trim() || !form.visible_until) {
      toast.error("Please complete title, description, announced by, and visible until.");
      return;
    }

    setSaving(true);

    try {
      if (editId) {
        await UserServices.updateAnnouncement(editId, form);
        toast.success("Announcement updated successfully.");
      } else {
        await UserServices.addAnnouncement({
          ...form,
          branch_id: normalizedBranchId,
        });
        toast.success("Announcement created successfully.");
      }

      resetForm();
      await loadAnnouncements();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to save announcement.");
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (id, action, message) => {
    try {
      await UserServices.announcementActions(id, action);
      toast.success(message);
      if (String(selectedId) === String(id) && action === "2") {
        setSelectedId(null);
      }
      await loadAnnouncements();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to update announcement status.");
    }
  };

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design">
          <MenuButton />
          <div className="announcement-shell">
            <section className="announcement-hero">
              <div>
                <span className="announcement-eyebrow">Announcements</span>
                <h2>Announcement Manager</h2>
                <p>
                  Publish branch or global announcements for all employees and keep important
                  notices visible until their deadline.
                </p>
              </div>
              <div className="announcement-hero-chip">
                {normalizedBranchId ? "Branch Announcement Mode" : "Global Announcement Mode"}
              </div>
            </section>

            <section className="announcement-grid">
              <div className="announcement-card">
                <div className="announcement-card-header">
                  <div>
                    <span className="announcement-section-label">Editor</span>
                    <h4>{editId ? "Update Announcement" : "Create Announcement"}</h4>
                  </div>
                </div>

                <form className="announcement-form" onSubmit={handleSave}>
                  <input
                    className="form-control"
                    placeholder="Announcement Title"
                    value={form.title}
                    onChange={(event) => setForm({ ...form, title: event.target.value })}
                  />
                  <input
                    className="form-control"
                    placeholder="Announced By"
                    value={form.announced_by}
                    onChange={(event) => setForm({ ...form, announced_by: event.target.value })}
                  />
                  <select
                    className="form-select"
                    value={form.priority}
                    onChange={(event) => setForm({ ...form, priority: event.target.value })}
                  >
                    <option value="High">High Priority</option>
                    <option value="Medium">Medium Priority</option>
                    <option value="Low">Low Priority</option>
                  </select>
                  <input
                    className="form-control"
                    type="datetime-local"
                    value={form.visible_from}
                    onChange={(event) => setForm({ ...form, visible_from: event.target.value })}
                  />
                  <input
                    className="form-control"
                    type="datetime-local"
                    value={form.visible_until}
                    onChange={(event) => setForm({ ...form, visible_until: event.target.value })}
                  />
                  <textarea
                    className="form-control announcement-textarea"
                    rows="7"
                    placeholder="Announcement description"
                    value={form.description}
                    onChange={(event) => setForm({ ...form, description: event.target.value })}
                  />
                  <div className="announcement-form-actions">
                    <button className="btn btn-primary" disabled={saving}>
                      {saving ? "Saving..." : editId ? "Update Announcement" : "Save Announcement"}
                    </button>
                    {editId ? (
                      <button type="button" className="btn btn-light" onClick={resetForm}>
                        Cancel Edit
                      </button>
                    ) : null}
                  </div>
                </form>
              </div>

              <div className="announcement-card">
                <div className="announcement-card-header">
                  <div>
                    <span className="announcement-section-label">Preview</span>
                    <h4>{selectedAnnouncement?.title || "Announcement Preview"}</h4>
                    <p>
                      {selectedAnnouncement?.announced_by
                        ? `Announced by ${selectedAnnouncement.announced_by}`
                        : "Select an announcement to preview it here."}
                    </p>
                  </div>
                </div>

                {selectedAnnouncement ? (
                  <div className="announcement-preview">
                    <div className="announcement-preview-top">
                      <span className={priorityClass(selectedAnnouncement.priority)}>
                        {selectedAnnouncement.priority} Priority
                      </span>
                      <span className={statusClass(isLive(selectedAnnouncement) ? "live" : "scheduled")}>
                        {isLive(selectedAnnouncement) ? "Live Now" : "Not Live"}
                      </span>
                    </div>
                    <h3>{selectedAnnouncement.title}</h3>
                    <div className="announcement-preview-meta">
                      <span>By: {selectedAnnouncement.announced_by || "N/A"}</span>
                      <span>From: {formatDateTime(selectedAnnouncement.visible_from)}</span>
                      <span>Until: {formatDateTime(selectedAnnouncement.visible_until)}</span>
                    </div>
                    <div className="announcement-preview-body">{selectedAnnouncement.description}</div>
                  </div>
                ) : (
                  <div className="announcement-empty">Create or select an announcement to preview it.</div>
                )}
              </div>
            </section>

            <section className="announcement-card">
              <div className="announcement-card-header announcement-card-header-spread">
                <div>
                  <span className="announcement-section-label">Library</span>
                  <h4>Saved Announcements</h4>
                </div>
                <div className="announcement-toolbar">
                  <input
                    className="form-control"
                    placeholder="Search announcements"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                  <select
                    className="form-select"
                    value={priorityFilter}
                    onChange={(event) => setPriorityFilter(event.target.value)}
                  >
                    <option value="">All Priorities</option>
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                  <select
                    className="form-select"
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                  >
                    <option value="2">All Active + Inactive</option>
                    <option value="1">Active Only</option>
                    <option value="0">Inactive Only</option>
                  </select>
                </div>
              </div>

              {loading ? (
                <div className="announcement-empty">Loading announcements...</div>
              ) : announcements.length ? (
                <div className="announcement-list">
                  {announcements.map((row) => (
                    <div
                      key={row.id}
                      className={`announcement-list-item ${String(selectedId) === String(row.id) ? "active" : ""}`}
                    >
                      <button
                        type="button"
                        className="announcement-list-select"
                        onClick={() => setSelectedId(row.id)}
                      >
                        <div className="announcement-list-top">
                          <strong>{row.title}</strong>
                          <span className={priorityClass(row.priority)}>{row.priority}</span>
                        </div>
                        <div className="announcement-list-meta">
                          <span>{row.announced_by || "N/A"}</span>
                          <span>Until {formatDateTime(row.visible_until)}</span>
                        </div>
                        <p>{row.description}</p>
                      </button>
                      <div className="announcement-list-actions">
                        <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => beginEdit(row)}>
                          Edit
                        </button>
                        {row.status === "1" ? (
                          <button
                            type="button"
                            className="btn btn-outline-warning btn-sm"
                            onClick={() => updateStatus(row.id, "0", "Announcement inactive now.")}
                          >
                            Inactivate
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-outline-success btn-sm"
                            onClick={() => updateStatus(row.id, "1", "Announcement activated now.")}
                          >
                            Activate
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => updateStatus(row.id, "2", "Announcement deleted now.")}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="announcement-empty">No announcements found for this branch yet.</div>
              )}
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}
